// Small static statutory-holiday lookup for the dashboard mini-calendar.
//
// Intentionally minimal: covers the federal/national observances most
// likely to bump a game date for the countries Game Day actually has
// teams in today (CA, US, GB). External APIs would be more accurate
// but cost a network round-trip on every dashboard render — for a
// glanceable "heads up, this is a long weekend" indicator a static
// set is the right trade.
//
// Each entry is `{ date: 'YYYY-MM-DD', name: 'Display name' }`.
// Variable-date holidays (Easter, Thanksgiving's Mon-of-Oct in CA,
// Memorial-Day's last-Mon-of-May in US, etc.) are pre-computed for
// the current year + the two flanking years. That covers the rolling
// window the mini-calendar can scroll to without exploding the
// dataset. When 2027 rolls around, refresh this file with the next
// year's dates.
//
// Country codes are lowercase ISO-3166-1 alpha-2, matching how
// teams.settings.country is stored.

// ── Helpers for variable-date holidays ────────────────────────────────

// Nth occurrence of a weekday in a month. n=1 → first, n=-1 → last.
// dayOfWeek: 0=Sun..6=Sat.
function nthDow(year, monthIdx, dayOfWeek, n) {
  if (n > 0) {
    const first = new Date(year, monthIdx, 1);
    const offset = (dayOfWeek - first.getDay() + 7) % 7;
    const d = 1 + offset + (n - 1) * 7;
    return new Date(year, monthIdx, d);
  }
  // n < 0 — count from end of month
  const last = new Date(year, monthIdx + 1, 0);
  const offset = (last.getDay() - dayOfWeek + 7) % 7;
  const d = last.getDate() - offset + (n + 1) * 7;
  return new Date(year, monthIdx, d);
}

// May 8 2026 (v2.5.87) — Monday on-or-before a given date. Used for
// Canadian Victoria Day, defined by statute as "the Monday next
// preceding the 25th day of May" — i.e., the Monday in the May
// 18-24 window each year. The previous implementation reused
// nthDow(_, 4, 1, -1) (last Monday of May) and noted "close
// enough" in a code comment, but that's wrong every year, not just
// when May 25 falls on a Monday. Verified against Government of
// Canada published dates 2024-2028.
function mondayOnOrBefore(year, monthIdx, day) {
  const d = new Date(year, monthIdx, day);
  const dow = d.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
  // Distance back to most recent Monday: Mon=0, Tue=1, ..., Sun=6
  const offset = (dow + 6) % 7;
  d.setDate(day - offset);
  return d;
}

function fmt(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// ── Country generators ────────────────────────────────────────────────

function holidaysCA(year) {
  return [
    { date: `${year}-01-01`,                 name: "New Year's Day" },
    { date: fmt(nthDow(year, 1, 1, 3)),      name: 'Family Day' }, // 3rd Mon Feb (most provinces)
    { date: fmt(mondayOnOrBefore(year, 4, 24)), name: 'Victoria Day' }, // Monday on or before May 24
    { date: `${year}-07-01`,                 name: 'Canada Day' },
    { date: fmt(nthDow(year, 7, 1, 1)),      name: 'Civic Holiday' }, // 1st Mon Aug
    { date: fmt(nthDow(year, 8, 1, 1)),      name: 'Labour Day' },
    { date: `${year}-09-30`,                 name: 'Truth & Reconciliation' },
    { date: fmt(nthDow(year, 9, 1, 2)),      name: 'Thanksgiving' }, // 2nd Mon Oct
    { date: `${year}-11-11`,                 name: 'Remembrance Day' },
    { date: `${year}-12-25`,                 name: 'Christmas Day' },
    { date: `${year}-12-26`,                 name: 'Boxing Day' },
  ];
}

function holidaysUS(year) {
  return [
    { date: `${year}-01-01`,             name: "New Year's Day" },
    { date: fmt(nthDow(year, 0, 1, 3)),  name: 'MLK Jr. Day' },
    { date: fmt(nthDow(year, 1, 1, 3)),  name: "Presidents' Day" },
    { date: fmt(nthDow(year, 4, 1, -1)), name: 'Memorial Day' },
    { date: `${year}-06-19`,             name: 'Juneteenth' },
    { date: `${year}-07-04`,             name: 'Independence Day' },
    { date: fmt(nthDow(year, 8, 1, 1)),  name: 'Labor Day' },
    { date: fmt(nthDow(year, 9, 1, 2)),  name: 'Columbus Day' },
    { date: `${year}-11-11`,             name: 'Veterans Day' },
    { date: fmt(nthDow(year, 10, 4, 4)), name: 'Thanksgiving' }, // 4th Thu Nov
    { date: `${year}-12-25`,             name: 'Christmas Day' },
  ];
}

function holidaysGB(year) {
  return [
    { date: `${year}-01-01`,             name: "New Year's Day" },
    { date: fmt(nthDow(year, 4, 1, 1)),  name: 'Early May Bank Holiday' },
    { date: fmt(nthDow(year, 4, 1, -1)), name: 'Spring Bank Holiday' },
    { date: fmt(nthDow(year, 7, 1, -1)), name: 'Summer Bank Holiday' },
    { date: `${year}-12-25`,             name: 'Christmas Day' },
    { date: `${year}-12-26`,             name: 'Boxing Day' },
  ];
}

const GENERATORS = {
  ca: holidaysCA,
  us: holidaysUS,
  gb: holidaysGB,
};

/**
 * Get a Map<YYYY-MM-DD, holidayName> for the given country, covering
 * the year before, the current year, and the year after — wide enough
 * for the dashboard mini-calendar to scroll without hitting blanks.
 *
 * country: lowercase ISO-3166-1 alpha-2 (e.g. 'ca', 'us'). Anything
 * unrecognised → empty Map (calendar shows nothing).
 */
export function getHolidayMap(country) {
  const code = String(country || '').toLowerCase();
  const gen = GENERATORS[code];
  if (!gen) return new Map();
  const year = new Date().getFullYear();
  const all = [
    ...gen(year - 1),
    ...gen(year),
    ...gen(year + 1),
  ];
  const m = new Map();
  for (const h of all) m.set(h.date, h.name);
  return m;
}

/**
 * Country codes we have holiday data for. Used to gate UI features
 * that depend on the holiday set (e.g. legend entries) so the
 * calendar doesn't render an empty-looking holiday legend for
 * teams in countries we don't cover.
 */
export const SUPPORTED_HOLIDAY_COUNTRIES = Object.keys(GENERATORS);
